import React from 'react';
import { ExternalLink, Shield, BookOpen, FileText, Building, Briefcase, Globe, ShieldAlert, CreditCard } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';

const Resources = () => {
  const { language } = useLanguage();

  const resources = {
    en: [
      {
        name: 'Make a Payment',
        description: 'Pay your insurance or premium finance bill online',
        url: '/payment',
        icon: CreditCard,
        internal: true
      },
      {
        name: 'Insurance Terms Glossary — NAIC',
        description: 'Insurance definitions and terminology (English resource)',
        url: 'https://content.naic.org/glossary-insurance-terms',
        icon: BookOpen
      },
      {
        name: 'Consumer Protection & Fraud Prevention',
        description: 'Texas Attorney General - Consumer Protection',
        url: 'https://www.texasattorneygeneral.gov/consumer-protection',
        icon: ShieldAlert
      },
      {
        name: 'FEMA',
        description: 'Federal Emergency Management Agency',
        url: 'https://www.fema.gov',
        icon: Shield
      },
      {
        name: 'NIPR',
        description: 'National Insurance Producer Registry',
        url: 'https://nipr.com',
        icon: FileText
      },
      {
        name: 'OCS Puerto Rico',
        description: 'Office of the Commissioner of Insurance',
        url: 'https://www.ocs.pr.gov',
        icon: Building
      },
      {
        name: 'Florida CFO',
        description: 'Florida Chief Financial Officer',
        url: 'https://myfloridacfo.com',
        icon: Briefcase
      },
      {
        name: 'TDI Texas',
        description: 'Texas Department of Insurance',
        url: 'https://www.tdi.texas.gov/index.html',
        icon: BookOpen
      },
      {
        name: 'NAIC',
        description: 'National Association of Insurance Commissioners',
        url: 'https://content.naic.org',
        icon: Globe
      },
      {
        name: 'Members of LAAIA Dallas Chapter',
        description: 'Professional association membership',
        url: 'https://www.laaiadallas.com',
        icon: Briefcase,
        note: 'Professional association, not a regulator or guarantor of policies'
      }
    ],
    es: [
      {
        name: 'Realizar un Pago',
        description: 'Pague su seguro o financiamiento de prima en línea',
        url: '/payment',
        icon: CreditCard,
        internal: true
      },
      {
        name: 'Glosario de términos de seguros — NAIC',
        description: 'Definiciones de seguros (recurso externo en inglés)',
        url: 'https://content.naic.org/glossary-insurance-terms',
        icon: BookOpen
      },
      {
        name: 'Protección al Consumidor y Prevención de Fraude',
        description: 'Procuraduría General de Texas - Protección al Consumidor',
        url: 'https://www.texasattorneygeneral.gov/consumer-protection',
        icon: ShieldAlert
      },
      {
        name: 'FEMA',
        description: 'Agencia Federal para el Manejo de Emergencias',
        url: 'https://www.fema.gov',
        icon: Shield
      },
      {
        name: 'NIPR',
        description: 'Registro Nacional de Productores de Seguros',
        url: 'https://nipr.com',
        icon: FileText
      },
      {
        name: 'OCS Puerto Rico',
        description: 'Oficina del Comisionado de Seguros',
        url: 'https://www.ocs.pr.gov',
        icon: Building
      },
      {
        name: 'Florida CFO',
        description: 'Jefe de Finanzas de Florida',
        url: 'https://myfloridacfo.com',
        icon: Briefcase
      },
      {
        name: 'TDI Texas',
        description: 'Departamento de Seguros de Texas',
        url: 'https://www.tdi.texas.gov/index.html',
        icon: BookOpen
      },
      {
        name: 'NAIC',
        description: 'Asociación Nacional de Comisionados de Seguros',
        url: 'https://content.naic.org',
        icon: Globe
      },
      {
        name: 'Miembros de LAAIA Dallas Chapter',
        description: 'Membresía de asociación profesional',
        url: 'https://www.laaiadallas.com',
        icon: Briefcase,
        note: 'Asociación profesional, no es regulador ni garante de pólizas'
      }
    ]
  };

  const resourceList = resources[language];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {language === 'en' ? 'Helpful Resources' : 'Recursos Útiles'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {language === 'en'
              ? 'Access important insurance and regulatory resources to stay informed and protected.'
              : 'Acceda a recursos importantes de seguros y regulatorios para mantenerse informado y protegido.'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {resourceList.map((resource, index) => {
            const Icon = resource.icon;
            const isInternal = resource.internal;
            
            const cardContent = (
              <Card className="h-full group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-gray-100 hover:border-blue-300">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded-lg flex items-center justify-center group-hover:from-blue-600 group-hover:to-blue-700 transition-all duration-300">
                      <Icon className="w-6 h-6 text-blue-700 group-hover:text-white transition-colors" />
                    </div>
                    {!isInternal && (
                      <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                    )}
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors mb-2">
                      {resource.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {resource.description}
                    </p>
                    {resource.note && (
                      <p className="text-xs text-gray-500 italic mt-2">
                        {resource.note}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-blue-600 font-medium text-sm group-hover:gap-3 transition-all">
                    {isInternal 
                      ? (language === 'en' ? 'View Page' : 'Ver Página')
                      : (language === 'en' ? 'Visit Website' : 'Visitar Sitio')}
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </CardContent>
              </Card>
            );

            return isInternal ? (
              <Link key={index} to={resource.url} className="block">
                {cardContent}
              </Link>
            ) : (
              <a
                key={index}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                {cardContent}
              </a>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 max-w-2xl mx-auto">
            {language === 'en'
              ? 'These resources provide valuable information about insurance regulations, disaster preparedness, and consumer protection.'
              : 'Estos recursos proporcionan información valiosa sobre regulaciones de seguros, preparación para desastres y protección al consumidor.'}
          </p>
        </div>

        {/* Palerm Informa - YouTube Channel */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Palerm Informa
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'en' 
                ? 'Conversations about insurance and our community' 
                : 'Conversaciones sobre seguros y nuestra comunidad'}
            </p>
          </div>

          <div className="flex justify-center">
            <a
              href="https://www.youtube.com/channel/UCekpZhTu76dUGvh6hHdoyFw"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 group focus:outline-none focus:ring-4 focus:ring-red-300"
              aria-label={language === 'en' ? 'Watch Palerm Informa on YouTube' : 'Ver Palerm Informa en YouTube'}
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              {language === 'en' ? 'Watch on YouTube' : 'Ver en YouTube'}
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Resources;